import { jsx as _jsx } from "react/jsx-runtime";
import noop from "lodash/noop";
import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
const defaultValue = {
    setVideoEl: noop,
    pause: noop,
    play: noop,
    setCurrentTime: noop
};
const VideoControlContext = /*#__PURE__*/ createContext(defaultValue);
export const VideoControlProvider = (param)=>{
    let { children } = param;
    const videoRef = useRef();
    const setVideoEl = useCallback((videoEl)=>videoRef.current = videoEl, []);
    const pause = useCallback(()=>{
        if (videoRef.current && !videoRef.current.paused) {
            videoRef.current.pause();
        }
    }, []);
    const play = useCallback(()=>{
        var _videoRef_current;
        if ((_videoRef_current = videoRef.current) === null || _videoRef_current === void 0 ? void 0 : _videoRef_current.paused) {
            var _videoRef_current1;
            videoRef === null || videoRef === void 0 ? void 0 : (_videoRef_current1 = videoRef.current) === null || _videoRef_current1 === void 0 ? void 0 : _videoRef_current1.play();
        }
    }, []);
    const setCurrentTime = useCallback((time)=>{
        if (videoRef.current) {
            videoRef.current.currentTime = time;
            if (videoRef.current.paused) {
                // setting currentTime has a delay if that video chunk is not loaded yet
                // But if we call play (and pause) it will force the video to go to the time even if it is not preloaded
                videoRef.current.play().catch(()=>{
                // an error will be thrown if the video is not loaded yet and we call pause just after play
                // we can safely ignore it
                });
                videoRef.current.pause();
            }
        }
    }, []);
    const contextValue = useMemo(()=>({
            setVideoEl,
            pause,
            play,
            setCurrentTime
        }), [
        pause,
        play,
        setCurrentTime,
        setVideoEl
    ]);
    return /*#__PURE__*/ _jsx(VideoControlContext.Provider, {
        value: contextValue,
        children: children
    });
};
export function useVideoControlContext() {
    const context = useContext(VideoControlContext);
    if (context === defaultValue) {
        const error = "VideoControlContext used outside of VideoControlProvider";
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
}
